import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';

import { match } from 'ts-pattern';

import { cn } from '@nesso/ui/lib/utils';
import { CheckboxField } from '@nesso/ui/primitives/checkbox-field';

import { slugify } from '@/shared/lib/format-utils';

export type CheckboxesListOption = {
    label: string;
    value: string;
};

export type Props = {
    options: CheckboxesListOption[];
    values: string[];
    onValuesChange: (values: string[]) => void;
    columns?: 1 | 2;
    showOnlySelected?: boolean;
    noWrapper?: boolean;
    extras?: ReactNode;
    className?: string;
};

export function CheckboxList({
    options,
    values,
    onValuesChange,
    columns = 2,
    showOnlySelected,
    noWrapper = false,
    extras,
    className,
}: Props) {
    const listId = useId();

    // Snapshot the values at the moment showOnlySelected becomes true so unchecking
    // a visible option keeps it on screen until the user toggles back to "show all".
    const [visibleValues, setVisibleValues] = useState(values);
    const valuesRef = useRef(values);
    valuesRef.current = values;
    useEffect(() => {
        if (showOnlySelected) setVisibleValues(valuesRef.current);
    }, [showOnlySelected]);

    const filteredOptions = useMemo(
        () =>
            showOnlySelected
                ? options.filter((option) => visibleValues.includes(option.value))
                : options,
        [showOnlySelected, options, visibleValues],
    );

    const toggle = (value: string) => {
        const isChecked = values.includes(value);
        const updated = isChecked ? values.filter((v) => v !== value) : [...values, value];
        onValuesChange(updated);
    };

    const items = filteredOptions.map((option) => {
        const isChecked = values.includes(option.value);
        return (
            <CheckboxField
                key={option.value}
                id={`${listId}${slugify(option.value)}`}
                label={option.label}
                checked={isChecked}
                onCheckedChange={() => toggle(option.value)}
            />
        );
    });

    return match(noWrapper)
        .with(true, () => (
            <>
                {items}
                {extras}
            </>
        ))
        .otherwise(() => (
            <div
                className={cn(
                    columns === 1
                        ? 'flex flex-col gap-1'
                        : 'flex flex-col gap-1 xs:grid xs:grid-cols-[repeat(2,15rem)] xs:gap-x-4 xs:gap-y-1',
                    className,
                )}
            >
                {items}
                {extras}
            </div>
        ));
}
